import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Project } from '@captain-tasko/shared';
import { apiList } from '../lib/api.js';
import { NotFound } from './NotFound.js';

// ProjectShortRedirect — CTSK-235 Phase 2a.
//
// Handles /pj/:key by fetching the projects list (small: ~15 rows today, no
// pagination needed for the org's project catalogue) and finding the row whose
// canonical `key` matches the URL-supplied key case-insensitively. On success,
// replaces the URL with the canonical /projects/<ULID> form so the browser
// location bar reflects the durable link.
//
// Why client-side list-filter rather than a server GET /projects/by-key/:key?
// - Projects list is tiny and the endpoint's response is aggressively cached
//   by React Query, so the round-trip cost is effectively zero after first hit.
// - No server-side changes → no api rebuild, no migration, keeps the ship inside
//   the web bundle (matches operator's CI/CD-quota constraint for the batched push).
// - When the project count grows past ~100, this pattern should be swapped for
//   a dedicated by-key endpoint — filed as a follow-up if the count crosses that.
//
// Query params are preserved across the redirect (AC#3 parity with Phase 1):
// /pj/CTSK?tab=sessions → /projects/<ULID>?tab=sessions.
//
// externalId disambiguation stays out of scope here — projects don't carry
// externalIds today (they were introduced for workitems in CTSK-84; project
// keys are unique per org by construction).
//
// Backend attribution unchanged per AC#7 — this is web-app routing only; no
// new server-side route.

export interface ProjectShortRedirectProps {
  projectKey: string;
  queryString: string;
  onNavigate: (path: string) => void;
}

export function ProjectShortRedirect({ projectKey, queryString, onNavigate }: ProjectShortRedirectProps) {
  const { data, error, isLoading } = useQuery({
    queryKey: ['pj-short-redirect', 'projects-list'],
    // The projects list is a shared cache — reuse whatever ProjectsList /
    // sidebar/dashboard already fetched. React Query dedupes by queryKey.
    queryFn: () => apiList<Project>('/projects'),
    staleTime: 30_000,
    retry: false,
  });

  const needle = projectKey.trim().toLowerCase();
  const match = data?.data.find((p) => p.key.trim().toLowerCase() === needle) ?? null;

  useEffect(() => {
    if (!match) return;
    const params = new URLSearchParams(queryString.startsWith('?') ? queryString.slice(1) : queryString);
    const suffix = params.toString();
    const dest = `/projects/${match.id}${suffix ? `?${suffix}` : ''}`;
    if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', dest);
    }
    onNavigate(dest);
  }, [match, queryString, onNavigate]);

  if (isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
        Resolving project <code>{projectKey}</code>…
      </div>
    );
  }

  if (error || (data && !match)) {
    // No project matched the key. NotFound explains the shape so the reader
    // knows /pj/ expects a canonical project key like "CTSK" or "AE".
    return <NotFound path={`/pj/${projectKey}`} />;
  }

  return null;
}
